import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Sparkles, Edit3, Eye, EyeOff, Check, X, Home } from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const displayLabel = (s) => s.custom_label || s.label;

export default function PortalsOverlay({ sections, onSelect, isNight, onSectionUpdated, onReturnToSplash }) {
    const accent = isNight ? "#D4AF37" : "#B89B5E";

    return (
        <div
            data-testid="portals-overlay"
            className="absolute inset-0 z-20 pointer-events-auto overflow-y-auto px-4 md:px-10 pb-16"
            style={{
                background: isNight
                    ? "radial-gradient(ellipse at top, rgba(5,5,12,0.55), rgba(0,0,0,0.85) 70%)"
                    : "radial-gradient(ellipse at top, rgba(255,255,255,0.55), rgba(245,240,228,0.85) 70%)",
                backdropFilter: "blur(12px)",
            }}
        >
            {/* Return to The Estate (splash) */}
            {onReturnToSplash && (
                <button
                    type="button"
                    onClick={onReturnToSplash}
                    data-testid="portals-return-to-splash"
                    className="absolute top-28 right-6 md:right-12 z-30 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase transition-all hover:scale-[1.03]"
                    style={{
                        background: accent,
                        color: "#0a0a0a",
                        boxShadow: `0 10px 30px -10px ${accent}`,
                    }}
                >
                    <Home size={12} />
                    The Estate
                </button>
            )}

            <div className="max-w-7xl mx-auto pt-36 md:pt-44 fade-in">
                <div className="text-center mb-12 md:mb-16">
                    <div
                        className="inline-flex items-center gap-2 text-[10px] tracking-[0.45em] uppercase mb-4"
                        style={{ color: accent }}
                    >
                        <Sparkles size={12} /> Portals <Sparkles size={12} />
                    </div>
                    <h1
                        className="font-serif-display text-5xl md:text-7xl uppercase tracking-luxe leading-none"
                        style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                    >
                        Choose Your Realm
                    </h1>
                    <p
                        className="mt-5 max-w-xl mx-auto text-sm md:text-base font-light tracking-wide"
                        style={{ color: isNight ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)" }}
                    >
                        Step through any portal. Click the pencil to rename. Tap the eye to hide a portal from the menu.
                    </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {sections.map((s, idx) => (
                        <PortalCard
                            key={s.id}
                            section={s}
                            isNight={isNight}
                            accent={accent}
                            onClick={() => onSelect(s.id)}
                            onSectionUpdated={onSectionUpdated}
                            delay={idx * 70}
                        />
                    ))}
                </div>

                <div
                    className="mt-16 text-center text-[10px] tracking-[0.4em] uppercase fade-in-slow"
                    style={{ color: isNight ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.4)" }}
                >
                    Caprio Chateau · The Estate of Eternal Light
                </div>
            </div>
        </div>
    );
}

function PortalCard({ section, isNight, accent, onClick, onSectionUpdated, delay }) {
    const [editing, setEditing] = useState(false);
    const [draftName, setDraftName] = useState(displayLabel(section));
    const [saving, setSaving] = useState(false);
    const { isAdmin } = useAdmin();

    const hidden = !!section.hidden_in_menu;

    const saveName = async (e) => {
        e?.stopPropagation();
        if (saving) return;
        setSaving(true);
        try {
            const res = await axios.patch(`${API}/sections/${section.id}`, {
                custom_label: draftName.trim() || section.label,
            });
            onSectionUpdated && onSectionUpdated(res.data);
            setEditing(false);
        } catch (err) { console.error(err); } finally { setSaving(false); }
    };

    const toggleHidden = async (e) => {
        e.stopPropagation();
        try {
            const res = await axios.patch(`${API}/sections/${section.id}`, {
                hidden_in_menu: !hidden,
            });
            onSectionUpdated && onSectionUpdated(res.data);
        } catch (err) { console.error(err); }
    };

    return (
        <div
            data-testid={`portal-card-${section.id}`}
            onClick={() => !editing && onClick && onClick()}
            className="group relative aspect-[4/5] rounded-2xl overflow-hidden text-left cursor-pointer fade-in"
            style={{
                animationDelay: `${delay}ms`,
                background: isNight
                    ? "linear-gradient(160deg, rgba(20,18,28,0.75), rgba(5,5,12,0.9))"
                    : "linear-gradient(160deg, rgba(255,255,255,0.85), rgba(235,228,210,0.7))",
                border: `1px solid ${accent}40`,
                boxShadow: isNight
                    ? `0 0 0 1px ${accent}25 inset, 0 30px 70px -30px ${accent}25`
                    : `0 0 0 1px ${accent}25 inset, 0 20px 50px -20px ${accent}30`,
                opacity: hidden ? 0.45 : 1,
            }}
        >
            <div
                className="absolute -inset-4 portal-ring opacity-60 group-hover:opacity-100 transition-opacity"
                style={{
                    background: `conic-gradient(from 0deg, transparent 0deg, ${accent}55 60deg, transparent 120deg, transparent 240deg, ${accent}33 300deg, transparent 360deg)`,
                    filter: "blur(18px)",
                }}
            />

            <div
                className="relative h-full w-full rounded-2xl overflow-hidden flex flex-col justify-between p-5"
                style={{
                    background: isNight
                        ? "linear-gradient(160deg, rgba(8,8,14,0.85), rgba(2,2,6,0.95))"
                        : "linear-gradient(160deg, rgba(255,255,255,0.92), rgba(240,233,218,0.85))",
                }}
            >
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div
                        className="absolute top-0 -left-1/2 w-[60%] h-full shimmer-line"
                        style={{
                            background: `linear-gradient(110deg, transparent 30%, ${accent}30 50%, transparent 70%)`,
                            filter: "blur(6px)",
                        }}
                    />
                </div>

                <div className="relative flex items-center justify-between">
                    <div
                        className="w-10 h-10 rounded-full grid place-items-center font-serif-display text-base"
                        style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}50` }}
                    >
                        {section.id.startsWith("p") && section.id.length <= 3
                            ? section.id.toUpperCase()
                            : displayLabel(section)[0].toUpperCase()}
                    </div>
                    <div className="flex items-center gap-1">
                        {isAdmin && (
                            <>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setDraftName(displayLabel(section)); setEditing(true); }}
                                    data-testid={`portal-rename-${section.id}`}
                                    className="w-7 h-7 rounded-full grid place-items-center"
                                    style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}45` }}
                                    title="Rename"
                                >
                                    <Edit3 size={11} />
                                </button>
                                <button
                                    type="button"
                                    onClick={toggleHidden}
                                    data-testid={`portal-hide-${section.id}`}
                                    className="w-7 h-7 rounded-full grid place-items-center"
                                    style={{
                                        background: hidden ? accent : `${accent}15`,
                                        color: hidden ? "#0a0a0a" : accent,
                                        border: `1px solid ${accent}45`,
                                    }}
                                    title={hidden ? "Unhide from menu" : "Hide from menu"}
                                >
                                    {hidden ? <EyeOff size={11} /> : <Eye size={11} />}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="relative">
                    <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: accent }}>
                        {hidden ? "Hidden in menu" : "Enter"}
                    </div>
                    {editing ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <input
                                value={draftName}
                                onChange={(e) => setDraftName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") saveName(e);
                                    if (e.key === "Escape") setEditing(false);
                                }}
                                autoFocus
                                data-testid={`portal-rename-input-${section.id}`}
                                className="flex-1 min-w-0 rounded-md px-2 py-1 text-sm outline-none"
                                style={{
                                    background: isNight ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                    color: isNight ? "#fff" : "#0a0a0a",
                                    border: `1px solid ${accent}80`,
                                }}
                            />
                            <button
                                type="button"
                                onClick={saveName}
                                data-testid={`portal-rename-save-${section.id}`}
                                className="w-7 h-7 rounded-md grid place-items-center"
                                style={{ background: accent, color: "#0a0a0a" }}
                            >
                                <Check size={12} />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setEditing(false); }}
                                data-testid={`portal-rename-cancel-${section.id}`}
                                className="w-7 h-7 rounded-md grid place-items-center"
                                style={{
                                    background: "transparent",
                                    color: isNight ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                                    border: `1px solid ${isNight ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
                                }}
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ) : (
                        <h3
                            className="font-serif-display text-xl md:text-2xl uppercase tracking-wide leading-tight"
                            style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                            data-testid={`portal-name-${section.id}`}
                        >
                            {displayLabel(section)}
                        </h3>
                    )}
                    {!editing && (
                        <div
                            className="mt-3 flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase opacity-70 group-hover:opacity-100 transition-opacity"
                            style={{ color: isNight ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }}
                        >
                            Portal
                            <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                        </div>
                    )}
                </div>

                <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ background: `radial-gradient(circle at 30% 80%, ${accent}30, transparent 60%)` }}
                />
            </div>
        </div>
    );
}
